/**
 * ColumnFilter: a funnel on a table column header that opens a checklist of
 * that column's values, the way Excel and Power BI filter a column.
 *
 * Presentational and reusable: it knows nothing about orders. The parent
 * passes the options, the currently selected values, and receives the new
 * selection on Apply. An empty selection means "no filter on this column",
 * never "nothing", so clearing every box and applying removes the filter.
 *
 * The menu is rendered in a portal at <body> and positioned from the
 * button's rectangle, because every console table sits in an overflow-x
 * scroller that would clip a menu rendered inside the header cell.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./table.css";

const MENU_WIDTH = 240;
const EDGE_GAP = 8;

const ColumnFilter = ({ label, options = [], selected = [], onApply, searchable = true }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(selected);
  const [query, setQuery] = useState("");
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const active = selected.length > 0;

  // Each opening starts from what is applied, not from a stale draft.
  const openMenu = () => {
    setDraft(selected);
    setQuery("");
    setOpen(true);
  };

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const left = Math.min(rect.left, window.innerWidth - MENU_WIDTH - EDGE_GAP);
    setPos({ top: rect.bottom + 6, left: Math.max(EDGE_GAP, left) });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (menuRef.current?.contains(e.target) || buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", () => setOpen(false), { once: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => String(o.label).toLowerCase().includes(q)) : options;
  }, [options, query]);

  const toggle = (value) =>
    setDraft((d) => (d.includes(value) ? d.filter((v) => v !== value) : [...d, value]));
  const selectVisible = () =>
    setDraft((d) => Array.from(new Set([...d, ...visible.map((o) => o.value)])));
  const clearAll = () => setDraft([]);

  const apply = () => {
    onApply(options.filter((o) => draft.includes(o.value)).map((o) => o.value));
    setOpen(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`lk-colfilter${active ? " is-active" : ""}`}
        onClick={() => (open ? setOpen(false) : openMenu())}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Filter ${label}${active ? `, ${selected.length} selected` : ""}`}
        title={active ? `${label}: ${selected.length} selected` : `Filter ${label}`}
      >
        <i className="fas fa-filter" aria-hidden="true" />
        {active && <span className="lk-colfilter-count">{selected.length}</span>}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="lk-colfilter-menu"
            role="dialog"
            aria-label={`Filter ${label}`}
            style={{ top: pos.top, left: pos.left, width: MENU_WIDTH }}
          >
            <div className="lk-colfilter-head">{label}</div>
            {searchable && options.length > 6 && (
              <input
                type="search"
                className="lk-colfilter-search"
                placeholder="Search values"
                value={query}
                autoFocus
                onChange={(e) => setQuery(e.target.value)}
              />
            )}
            <div className="lk-colfilter-bulk">
              <button type="button" onClick={selectVisible}>Select all</button>
              <button type="button" onClick={clearAll}>Clear</button>
            </div>
            <div className="lk-colfilter-list">
              {visible.length === 0 ? (
                <div className="lk-colfilter-empty">No matching values</div>
              ) : (
                visible.map((o) => (
                  <label key={o.value} className="lk-colfilter-item">
                    <input
                      type="checkbox"
                      checked={draft.includes(o.value)}
                      onChange={() => toggle(o.value)}
                    />
                    <span>{o.label}</span>
                  </label>
                ))
              )}
            </div>
            <div className="lk-colfilter-actions">
              <button type="button" className="lk-colfilter-cancel" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button type="button" className="lk-colfilter-apply" onClick={apply}>
                Apply
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default ColumnFilter;
